"use client";
import { Suspense, useState } from "react";
import { useSearchParams } from "next/navigation";
import Link from "next/link";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Check, MessageCircle, ShieldAlert } from "lucide-react";
import { useLocale } from "@/content/i18n/context";
import { t } from "@/content/i18n/messages";
import { getLocalizedPathname } from "@/lib/i18n";
import MarketingConsent from "@/components/forms/MarketingConsent";
import { appendMarketingConsent } from "@/lib/marketing-consent";
import Botcheck from "@/components/forms/Botcheck";
import { isBotSubmission } from "@/lib/botcheck";
import {
  DEFAULT_UNIT_QTY,
  SMALL_ORDER_NOTE,
  orderBandForUnits,
  sumUnits,
  withSmallPrefix,
} from "@/content/enterprise/order-minimum";
import {
  CONTROLLED_PART_PATTERN,
  gateNote,
  kycIntro,
  kycPrivacyNote,
  outputConsumers,
  primaryOperations,
  productLines,
  shipToTypes,
  YES_NO,
} from "@/content/enterprise/nvidia-gpu-kyc";
import { whatsappHref } from "@/lib/whatsapp";

// Web3Forms: SAME access key as the calculator, the general enterprise form and
// the DGX Spark form; DISTINCT format — request_type "NVIDIA Export KYC" and its
// own field set, so an export-screening submission is filterable in the inbox and
// never blurs with a brand lead. Spec: docs/research/enterprise-brand-directory.md [C8].
//
// The question set lives in content/enterprise/nvidia-gpu-kyc.ts. Read the header
// comment there before changing any field on the controlled branch — particularly
// the rule about never offering a dropdown that contains a plausible wrong answer.

interface PartLine {
  part: string;
  qty: string;
}

/** Every KYC answer in one object; ~25 useState calls would not be readable. */
const EMPTY_KYC = {
  registeredName: "",
  registration: "",
  registeredOffice: "",
  hqCountry: "",
  parentCompany: "",
  parentHq: "",
  localEntity: "",
  model: "",
  operation: "",
  operationOther: "",
  consumer: "",
  scale: "",
  site: "",
  resell: "",
  resellCustomer: "",
  remoteAccess: "",
  remoteCountries: "",
  priorPurchase: "",
  billingAddress: "",
  shipToAddress: "",
  shipToType: "",
  facility: "",
  installServices: "",
  thirdPartyFunding: "",
  fundingParty: "",
  paymentSource: "",
  compliancePolicy: "",
  securityProtocols: "",
};
type Kyc = typeof EMPTY_KYC;

const labelCls = "block text-xs font-semibold uppercase tracking-[0.14em] text-neutral-muted mb-2";
const inputCls =
  "w-full rounded-lg border border-border bg-card px-4 py-2.5 text-sm text-neutral-emphasis placeholder:text-neutral-muted/60 focus:border-primary focus:outline-none";
const pillCls = (active: boolean) =>
  `inline-flex min-h-11 items-center rounded-full border px-4 py-1.5 text-sm transition ${
    active
      ? "border-primary bg-primary/10 font-medium text-primary"
      : "border-border bg-card text-neutral-muted hover:border-primary/40 hover:text-primary"
  }`;

/**
 * Field/Choice/Group live at MODULE scope, not inside GpuEnquiryForm.
 *
 * Defining them in the component body makes React see a brand-new component type
 * on every keystroke, which remounts the <input> and drops focus after each
 * character — on a form this long, unusable. They read the locale from context
 * rather than taking it as a prop so no call site has to thread it.
 */
const OptionalTag = ({ locale }: { locale: ReturnType<typeof useLocale> }) => (
  <span className="ml-2 font-normal normal-case tracking-normal text-neutral-muted/70">
    {t(locale, "optional")}
  </span>
);

/** Label + text input. `hint` carries the example that makes a vague answer awkward. */
function Field({
  id,
  text,
  value,
  onChange,
  hint,
  optional,
  rows,
}: {
  id: string;
  text: string;
  value: string;
  onChange: (v: string) => void;
  hint?: string;
  optional?: boolean;
  rows?: number;
}) {
  const locale = useLocale();
  return (
    <div>
      <label htmlFor={id} className={labelCls}>
        {t(locale, text)}
        {optional && <OptionalTag locale={locale} />}
      </label>
      {rows ? (
        <textarea
          id={id}
          rows={rows}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={hint ? t(locale, hint) : undefined}
          className={inputCls}
        />
      ) : (
        <input
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={hint ? t(locale, hint) : undefined}
          className={inputCls}
        />
      )}
    </div>
  );
}

/** Label + a row of single-select pills. */
function Choice({
  text,
  options,
  value,
  onChange,
  optional,
}: {
  text: string;
  options: string[];
  value: string;
  onChange: (v: string) => void;
  optional?: boolean;
}) {
  const locale = useLocale();
  return (
    <div>
      <span className={labelCls}>
        {t(locale, text)}
        {optional && <OptionalTag locale={locale} />}
      </span>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => (
          <button key={o} type="button" onClick={() => onChange(o)} className={pillCls(value === o)}>
            {t(locale, o)}
          </button>
        ))}
      </div>
    </div>
  );
}

function Group({ n, title, children }: { n: string; title: string; children: React.ReactNode }) {
  const locale = useLocale();
  return (
    <fieldset className="rounded-lg border border-border bg-neutral-surface p-6">
      <legend className="px-2 font-display text-sm font-bold text-neutral-emphasis">
        {n} · {t(locale, title)}
      </legend>
      <div className="mt-2 space-y-6">{children}</div>
    </fieldset>
  );
}

function GpuEnquiryForm() {
  const locale = useLocale();
  const params = useSearchParams();
  const reduce = useReducedMotion();
  const inView = reduce
    ? {}
    : { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.4 } };
  const lp = (href: string) => getLocalizedPathname(href, locale);

  // ?part=B300 arrives from the general enterprise form's handoff notice. A part
  // that trips the controlled pattern also preselects the data-centre line, so the
  // buyer lands on the branch they were sent here for rather than re-picking it.
  const preselectPart = params.get("part") ?? "";
  const [line, setLine] = useState(
    CONTROLLED_PART_PATTERN.test(preselectPart) ? "data-centre-gpu" : "",
  );
  const [parts, setParts] = useState<PartLine[]>([{ part: preselectPart, qty: DEFAULT_UNIT_QTY }]);
  const [kyc, setKyc] = useState<Kyc>(EMPTY_KYC);
  const [name, setName] = useState("");
  const [role, setRole] = useState("");
  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");

  const set = (patch: Partial<Kyc>) => setKyc((prev) => ({ ...prev, ...patch }));
  const controlled = productLines.find((p) => p.id === line)?.controlled ?? false;

  const partsComplete = parts.some((p) => p.part.trim().length > 0);
  const contactComplete = Boolean(email.trim()) && Boolean(company.trim()) && Boolean(name.trim());

  /**
   * The gate. Required = the fields that carry diversion risk or that the
   * documentation cannot be prepared without. The compliance group (policy,
   * security protocols) and the ownership parent fields are deliberately NOT
   * here: they help the application, they do not decide whether we proceed.
   */
  const kycComplete =
    !controlled ||
    Boolean(
      kyc.registeredName.trim() &&
        kyc.registration.trim() &&
        kyc.registeredOffice.trim() &&
        kyc.hqCountry.trim() &&
        kyc.localEntity &&
        kyc.model.trim() &&
        kyc.operation &&
        (kyc.operation !== "Other" || kyc.operationOther.trim()) &&
        kyc.consumer &&
        kyc.scale.trim() &&
        kyc.site.trim() &&
        kyc.resell &&
        (kyc.resell !== "Yes" || kyc.resellCustomer.trim()) &&
        kyc.remoteAccess &&
        (kyc.remoteAccess !== "Yes" || kyc.remoteCountries.trim()) &&
        kyc.priorPurchase &&
        kyc.billingAddress.trim() &&
        kyc.shipToAddress.trim() &&
        kyc.shipToType &&
        (kyc.shipToType !== "A datacentre we contract with" || kyc.facility.trim()) &&
        kyc.thirdPartyFunding &&
        (kyc.thirdPartyFunding !== "Yes" || kyc.fundingParty.trim()) &&
        kyc.paymentSource.trim(),
    );

  const ready = Boolean(line) && partsComplete && contactComplete && kycComplete;
  // Data-centre GPUs are exempt from the 10-unit soft minimum: one 8-GPU HGX
  // system is already a large order, and a [SMALL] tag would bury it.
  const orderBand = orderBandForUnits(parts.map((p) => p.qty), controlled);

  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!ready) return;
    // Honeypot ticked: show success, send nothing.
    if (isBotSubmission(e.currentTarget)) {
      setStatus("sent");
      return;
    }
    setStatus("sending");
    const lineLabel = productLines.find((p) => p.id === line)?.label ?? line;
    // One readable line per part, mirroring the general form: "B300: 2 units".
    const configuration = parts
      .filter((p) => p.part.trim())
      .map((p) => `${p.part.trim()}: ${p.qty || "?"}`)
      .join("; ");
    const body = new FormData();
    body.append("access_key", process.env.NEXT_PUBLIC_WEB3FORMS_KEY ?? "");
    body.append("request_type", controlled ? "NVIDIA Export KYC" : "NVIDIA Enquiry");
    body.append("source", "nvidia-gpu-enquiry");
    body.append(
      "subject",
      withSmallPrefix(
        `${controlled ? "NVIDIA export KYC" : "NVIDIA enquiry"} — ${configuration} — ${company}`,
        orderBand,
      ),
    );
    body.append("from_name", "Anchor Sprint Website");
    body.append("product_line", lineLabel);
    body.append("configuration", configuration);
    body.append("name", name);
    body.append("role", role);
    body.append("company", company);
    body.append("email", email);
    body.append("phone", phone);
    if (controlled) {
      body.append("registered_name", kyc.registeredName);
      body.append("registration_number", kyc.registration);
      body.append("registered_office", kyc.registeredOffice);
      body.append("hq_country", kyc.hqCountry);
      body.append("parent_company", kyc.parentCompany);
      body.append("parent_hq", kyc.parentHq);
      body.append("entity_in_install_country", kyc.localEntity);
      body.append("model_or_software", kyc.model);
      body.append(
        "primary_operation",
        kyc.operation === "Other" ? kyc.operationOther : kyc.operation,
      );
      body.append("output_consumed_by", kyc.consumer);
      body.append("scale", kyc.scale);
      body.append("deployment_site", kyc.site);
      body.append("resell", kyc.resell);
      body.append("resell_customer", kyc.resellCustomer);
      body.append("third_party_access", kyc.remoteAccess);
      body.append("third_party_countries", kyc.remoteCountries);
      body.append("prior_purchase_this_class", kyc.priorPurchase);
      body.append("billing_address", kyc.billingAddress);
      body.append("ship_to_address", kyc.shipToAddress);
      body.append("ship_to_type", kyc.shipToType);
      body.append("facility", kyc.facility);
      body.append("installation_services", kyc.installServices);
      body.append("third_party_funding", kyc.thirdPartyFunding);
      body.append("funding_party", kyc.fundingParty);
      body.append("payment_source", kyc.paymentSource);
      body.append("trade_compliance_policy", kyc.compliancePolicy);
      body.append("security_protocols", kyc.securityProtocols);
    }
    body.append("order_band", orderBand);
    body.append("notes", notes);
    appendMarketingConsent(body, marketingConsent, locale);
    try {
      const res = await fetch("https://api.web3forms.com/submit", { method: "POST", body });
      if (!res.ok) throw new Error(String(res.status));
      setStatus("sent");
      if (typeof window !== "undefined" && "gtag" in window) {
        (window as unknown as { gtag: (...a: unknown[]) => void }).gtag("event", "nvidia_gpu_enquiry_submit", {
          product_line: lineLabel,
          export_controlled: controlled,
          brands: "NVIDIA",
          order_band: orderBand,
          quantity: sumUnits(parts.map((p) => p.qty)),
        });
      }
    } catch {
      setStatus("error");
    }
  };

  if (status === "sent") {
    return (
      <div className="rounded-lg border border-primary/30 bg-primary/5 p-10 text-center">
        <Check className="mx-auto h-10 w-10 text-primary" aria-hidden />
        <h2 className="mt-4 font-display text-2xl font-bold text-neutral-emphasis">
          {t(locale, "Enquiry received.")}
        </h2>
        <p className="mx-auto mt-3 max-w-md text-neutral-muted">
          {controlled
            ? t(
                locale,
                "We will review the end-use information and come back with the end-user documentation to sign, together with indicative pricing in RM, lead times and availability. Screening adds a few working days before a quotation can be released.",
              )
            : t(
                locale,
                "We will come back with a quotation in RM, usually within two working days — and we will say so if a different card is the better answer for the workload you described.",
              )}
        </p>
        <a
          href={whatsappHref("Hi Anchor Sprint, I just sent an NVIDIA GPU enquiry and have a follow-up question (from your NVIDIA enquiry page).")}
          target="_blank"
          rel="noopener noreferrer"
          data-track-event="contact_whatsapp_click"
          data-track-location="nvidia_enquiry_success"
          data-track-phone="601159246128"
          className="mt-6 inline-flex items-center gap-2 rounded-full border border-primary px-6 py-2.5 text-sm font-semibold text-primary transition hover:bg-primary/5"
        >
          <MessageCircle className="h-4 w-4" aria-hidden />
          {t(locale, "WhatsApp us")}
        </a>
      </div>
    );
  }

  return (
    <motion.form {...inView} onSubmit={submit} className="space-y-8">
      {/* Product line — first, because it decides whether the gate applies at all */}
      <div>
        <span className={labelCls}>{t(locale, "Which line are you buying?")}</span>
        <ul className="grid gap-2 sm:grid-cols-2">
          {productLines.map((p) => {
            const active = line === p.id;
            return (
              <li key={p.id}>
                <button
                  type="button"
                  onClick={() => setLine(p.id)}
                  aria-pressed={active}
                  className={`h-full w-full rounded-lg border p-4 text-left transition ${
                    active ? "border-primary bg-primary/5" : "border-border bg-card hover:border-primary/40"
                  }`}
                >
                  <span className="block text-sm font-semibold text-neutral-emphasis">
                    {t(locale, p.label)}
                  </span>
                  <span className="mt-1 block text-xs leading-relaxed text-neutral-muted">
                    {t(locale, p.detail)}
                  </span>
                  {p.controlled && (
                    <span className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-warning">
                      <ShieldAlert className="h-3.5 w-3.5" aria-hidden />
                      {t(locale, "Export documentation required")}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Configuration — kept directly above the end-use questions so a config that
          does not match the stated workload is visible in one glance, here and in
          the email that reaches us. */}
      <div>
        <span className={labelCls}>{t(locale, "Which parts, and how many?")}</span>
        <ul className="space-y-2">
          {parts.map((p, i) => (
            <li key={i} className="flex flex-wrap items-center gap-2">
              <input
                value={p.part}
                onChange={(e) =>
                  setParts((prev) => prev.map((x, j) => (j === i ? { ...x, part: e.target.value } : x)))
                }
                placeholder={t(locale, "Part — e.g. B300, H200, HGX B300 system")}
                aria-label={t(locale, "Part")}
                className={`${inputCls} flex-1 min-w-[14rem]`}
              />
              <input
                type="number"
                min={1}
                inputMode="numeric"
                value={p.qty}
                onChange={(e) =>
                  setParts((prev) => prev.map((x, j) => (j === i ? { ...x, qty: e.target.value } : x)))
                }
                placeholder={t(locale, "Qty")}
                aria-label={t(locale, "Quantity")}
                className="w-24 rounded-lg border border-border bg-card px-3 py-2.5 text-sm"
              />
              {parts.length > 1 && (
                <button
                  type="button"
                  onClick={() => setParts((prev) => prev.filter((_, j) => j !== i))}
                  className="inline-flex min-h-11 items-center px-2 text-sm text-neutral-muted hover:text-destructive"
                >
                  {t(locale, "Remove")}
                </button>
              )}
            </li>
          ))}
        </ul>
        <button
          type="button"
          onClick={() => setParts((prev) => [...prev, { part: "", qty: "" }])}
          className="mt-2 inline-flex min-h-11 items-center text-sm font-medium text-primary hover:underline"
        >
          + {t(locale, "Add another part")}
        </button>
        {orderBand === "small" && (
          <p
            role="status"
            className="mt-3 rounded-lg border border-info/30 bg-info/5 p-3 text-sm text-neutral-emphasis"
          >
            {t(locale, SMALL_ORDER_NOTE)}
          </p>
        )}
      </div>

      {controlled && (
        <>
          <div className="rounded-lg border border-warning/40 bg-warning/5 p-6">
            <p className="flex items-center gap-2 font-display text-sm font-bold text-neutral-emphasis">
              <ShieldAlert className="h-4 w-4 text-warning" aria-hidden />
              {t(locale, "Export controls")}
            </p>
            <p className="mt-3 text-sm leading-relaxed text-neutral-muted">{t(locale, kycIntro)}</p>
            <p className="mt-3 text-sm leading-relaxed text-neutral-muted">{t(locale, gateNote)}</p>
          </div>

          <Group n="01" title="Company and ownership">
            <div className="grid gap-6 sm:grid-cols-2">
              <Field
                id="kyc-reg-name"
                text="Full registered company name"
                value={kyc.registeredName}
                onChange={(v) => set({ registeredName: v })}
              />
              <Field
                id="kyc-reg-no"
                text="SSM or company registration number"
                value={kyc.registration}
                onChange={(v) => set({ registration: v })}
                hint="Registration number in your country of incorporation"
              />
            </div>
            <Field
              id="kyc-office"
              text="Registered office address"
              value={kyc.registeredOffice}
              onChange={(v) => set({ registeredOffice: v })}
              rows={2}
            />
            <div className="grid gap-6 sm:grid-cols-2">
              <Field
                id="kyc-hq"
                text="Headquarters location"
                value={kyc.hqCountry}
                onChange={(v) => set({ hqCountry: v })}
                hint="City and country"
              />
              <Field
                id="kyc-parent"
                text="Ultimate parent company"
                value={kyc.parentCompany}
                onChange={(v) => set({ parentCompany: v })}
                hint="Leave blank if there is none"
                optional
              />
            </div>
            <div className="grid gap-6 sm:grid-cols-2">
              <Field
                id="kyc-parent-hq"
                text="Parent company headquarters"
                value={kyc.parentHq}
                onChange={(v) => set({ parentHq: v })}
                optional
              />
              <Choice
                text="Do you hold a legal entity where the hardware will be installed?"
                options={YES_NO}
                value={kyc.localEntity}
                onChange={(v) => set({ localEntity: v })}
              />
            </div>
          </Group>

          {/* End use, decomposed. See the header comment in nvidia-gpu-kyc.ts:
              five narrow questions instead of one box we would have to police. */}
          <Group n="02" title="End use">
            <Field
              id="kyc-model"
              text="Which model or software will run on this?"
              value={kyc.model}
              onChange={(v) => set({ model: v })}
              hint="e.g. Llama 3 70B, Stable Diffusion, our own CFD solver"
            />
            <Choice
              text="Primary operation"
              options={primaryOperations}
              value={kyc.operation}
              onChange={(v) => set({ operation: v })}
            />
            {kyc.operation === "Other" && (
              <Field
                id="kyc-op-other"
                text="Describe the operation"
                value={kyc.operationOther}
                onChange={(v) => set({ operationOther: v })}
              />
            )}
            <Choice
              text="Who consumes the output?"
              options={outputConsumers}
              value={kyc.consumer}
              onChange={(v) => set({ consumer: v })}
            />
            {kyc.consumer === "Third parties given direct access to the hardware" && (
              <p className="text-sm text-neutral-muted">
                {t(
                  locale,
                  "Direct third-party access needs the countries and, where possible, the names of those end customers — please add them under third-party access below.",
                )}
              </p>
            )}
            <div className="grid gap-6 sm:grid-cols-2">
              <Field
                id="kyc-scale"
                text="Scale"
                value={kyc.scale}
                onChange={(v) => set({ scale: v })}
                hint="Users, requests per hour, or dataset size"
              />
              <Field
                id="kyc-site"
                text="Where will it physically run?"
                value={kyc.site}
                onChange={(v) => set({ site: v })}
                hint="Facility and city"
              />
            </div>
            <Choice
              text="Do you intend to resell the hardware?"
              options={YES_NO}
              value={kyc.resell}
              onChange={(v) => set({ resell: v })}
            />
            {kyc.resell === "Yes" && (
              <Field
                id="kyc-resell-customer"
                text="Intended customer — name and address"
                value={kyc.resellCustomer}
                onChange={(v) => set({ resellCustomer: v })}
                rows={2}
              />
            )}
            <Choice
              text="Will you provide remote access, cloud or as-a-Service to third parties?"
              options={YES_NO}
              value={kyc.remoteAccess}
              onChange={(v) => set({ remoteAccess: v })}
            />
            {kyc.remoteAccess === "Yes" && (
              <Field
                id="kyc-remote-countries"
                text="Countries served, and named end customers where possible"
                value={kyc.remoteCountries}
                onChange={(v) => set({ remoteCountries: v })}
                rows={2}
              />
            )}
            <Choice
              text="Have you purchased advanced computing hardware of this class before?"
              options={YES_NO}
              value={kyc.priorPurchase}
              onChange={(v) => set({ priorPurchase: v })}
            />
          </Group>

          <Group n="03" title="Delivery and deployment">
            <div className="grid gap-6 sm:grid-cols-2">
              <Field
                id="kyc-billing"
                text="Billing address"
                value={kyc.billingAddress}
                onChange={(v) => set({ billingAddress: v })}
                rows={2}
              />
              <Field
                id="kyc-shipto"
                text="Ship-to and installation address"
                value={kyc.shipToAddress}
                onChange={(v) => set({ shipToAddress: v })}
                rows={2}
              />
            </div>
            {/* A radio, not a free-text line: a forwarder or staging warehouse tells
                us something an address never would. */}
            <Choice
              text="What kind of location is the ship-to address?"
              options={shipToTypes}
              value={kyc.shipToType}
              onChange={(v) => set({ shipToType: v })}
            />
            {kyc.shipToType === "A datacentre we contract with" && (
              <Field
                id="kyc-facility"
                text="Facility name and address, and confirmation of sufficient power and cooling"
                value={kyc.facility}
                onChange={(v) => set({ facility: v })}
                rows={2}
              />
            )}
            <Choice
              text="Would you like deployment and installation included?"
              options={YES_NO}
              value={kyc.installServices}
              onChange={(v) => set({ installServices: v })}
              optional
            />
          </Group>

          <Group n="04" title="Funding">
            <Choice
              text="Is there third-party funding for this purchase?"
              options={YES_NO}
              value={kyc.thirdPartyFunding}
              onChange={(v) => set({ thirdPartyFunding: v })}
            />
            {kyc.thirdPartyFunding === "Yes" && (
              <Field
                id="kyc-funding-party"
                text="Funding party — name, location and relationship to you"
                value={kyc.fundingParty}
                onChange={(v) => set({ fundingParty: v })}
                rows={2}
              />
            )}
            <Field
              id="kyc-payment"
              text="Where will payment come from?"
              value={kyc.paymentSource}
              onChange={(v) => set({ paymentSource: v })}
              hint="The purchasing entity directly, or another source"
            />
          </Group>

          <Group n="05" title="Compliance">
            <Choice
              text="Do you have a trade compliance policy?"
              options={YES_NO}
              value={kyc.compliancePolicy}
              onChange={(v) => set({ compliancePolicy: v })}
              optional
            />
            <Field
              id="kyc-security"
              text="Security protocols for hardware of this class"
              value={kyc.securityProtocols}
              onChange={(v) => set({ securityProtocols: v })}
              hint="As much as you are able to share"
              rows={3}
              optional
            />
          </Group>
        </>
      )}

      <Group n={controlled ? "06" : "01"} title="Contact">
        <div className="grid gap-6 sm:grid-cols-2">
          <Field id="gpu-name" text="Your name" value={name} onChange={setName} />
          <Field id="gpu-role" text="Your role" value={role} onChange={setRole} optional />
          <Field id="gpu-company" text="Company" value={company} onChange={setCompany} />
          <Field id="gpu-email" text="Work email" value={email} onChange={setEmail} />
          <Field
            id="gpu-phone"
            text="Phone (WhatsApp optional)"
            value={phone}
            onChange={setPhone}
            optional
          />
        </div>
      </Group>

      <Field
        id="gpu-notes"
        text="Anything else — existing hardware, timeline, constraints"
        value={notes}
        onChange={setNotes}
        rows={3}
        optional
      />

      {controlled && <p className="text-sm text-neutral-muted">{t(locale, kycPrivacyNote)}</p>}

      <Botcheck />
      <MarketingConsent
        id="gpu-marketing-consent"
        checked={marketingConsent}
        onChange={setMarketingConsent}
        locale={locale}
      />

      <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center">
        <button
          type="submit"
          disabled={status === "sending" || !ready}
          className="inline-flex items-center gap-2 rounded-full bg-primary px-8 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary-emphasis disabled:cursor-not-allowed disabled:opacity-50"
        >
          {status === "sending" ? t(locale, "Sending…") : t(locale, "Request quotation")}
          <ArrowRight className="h-4 w-4" aria-hidden />
        </button>
        <a
          href={whatsappHref("Hi Anchor Sprint, I'd like to talk about NVIDIA GPUs before filling in the form (from your NVIDIA enquiry page).")}
          target="_blank"
          rel="noopener noreferrer"
          data-track-event="contact_whatsapp_click"
          data-track-location="nvidia_enquiry_form"
          data-track-phone="601159246128"
          className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
        >
          <MessageCircle className="h-4 w-4" aria-hidden />
          {t(locale, "Prefer to talk first? WhatsApp us")}
        </a>
      </div>

      {!line ? (
        <p className="text-sm text-neutral-muted">{t(locale, "Pick a line to continue.")}</p>
      ) : (
        !ready && (
          <p className="text-sm text-neutral-muted">
            {controlled && !kycComplete
              ? t(
                  locale,
                  "The export questions above are still incomplete — the quotation request stays disabled until they are answered.",
                )
              : t(locale, "Name at least one part, and your name, company and work email.")}
          </p>
        )
      )}
      {status === "error" && (
        <p className="text-sm text-destructive">
          {t(locale, "Something went wrong sending the form — please try again, or WhatsApp us instead.")}
        </p>
      )}
      <p className="text-sm text-neutral-muted">
        <Link href={lp("/enterprise/nvidia-malaysia/")} className="text-primary hover:underline">
          {t(locale, "Back to NVIDIA in Malaysia")}
        </Link>
        {" · "}
        <Link href={lp("/enterprise/dgx-spark-malaysia/enquiry/")} className="text-primary hover:underline">
          {t(locale, "Looking for DGX Spark instead?")}
        </Link>
      </p>
    </motion.form>
  );
}

export default function NvidiaGpuEnquiry() {
  const locale = useLocale();
  return (
    <main className="bg-background">
      <section className="pb-16 pt-24 lg:pt-28">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <p className="text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
            {t(locale, "NVIDIA GPU Enquiry")}
          </p>
          <h1 className="mt-5 font-display text-3xl font-bold leading-tight text-neutral-emphasis md:text-5xl">
            {t(locale, "NVIDIA GPU quotation — export documentation first")}
          </h1>
          <p className="mt-5 text-lg text-neutral-muted">
            {t(
              locale,
              "Data-centre accelerators need signed end-user documentation before pricing can be released. Answer the questions once here and we come back with indicative pricing in RM, lead times and availability. RTX cards, AI Enterprise licensing and networking are quoted without any of it.",
            )}
          </p>
          <div className="mt-12">
            {/* Static export: useSearchParams must sit inside Suspense so the shell
                prerenders — the proven blog-index pattern (PR #189). */}
            <Suspense fallback={null}>
              <GpuEnquiryForm />
            </Suspense>
          </div>
        </div>
      </section>
    </main>
  );
}
